import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Pagination } from './ui/pagination';
import { ListSkeleton, CardSkeleton } from './ui/skeleton';
import { usePagination } from '../hooks/usePagination';
import { useDebounce } from '../hooks/useDebounce';
import { useMobile } from '../hooks/useMobile';

export default function Tasks({ api, user }) {
  // Mobile detection
  const { isMobile, isTablet } = useMobile();
  
  // Basic state
  const [leads, setLeads] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filter, setFilter] = useState('all'); // all, pending, completed, overdue
  const [searchTerm, setSearchTerm] = useState('');
  
  // Search debouncing
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  
  // Pagination with API integration
  const fetchTasksWithParams = useCallback(async (params) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      limit: params.limit || (isMobile ? 10 : 20),
      ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
      ...(filter !== 'all' && { status: filter })
    });
    
    const response = await api.get(`/tasks?${queryParams}`);
    return response.data;
  }, [api, debouncedSearchTerm, filter, isMobile]);
  
  const {
    data: tasks,
    loading,
    error,
    pagination,
    goToPage,
    changePageSize,
    refresh,
    isEmpty,
    hasData
  } = usePagination(fetchTasksWithParams, { page: 1, limit: isMobile ? 10 : 20 });

  
  const [newTask, setNewTask] = useState({
    lead_id: '',
    title: '',
    description: '',
    due_date: ''
  });

  useEffect(() => {
    fetchLeads();
  }, []);

  // Reset to page 1 when filters change (usePagination auto-refetches via fetchTasksWithParams dependency)
  useEffect(() => {
    goToPage(1);
  }, [debouncedSearchTerm, filter]); // goToPage is stable

  const fetchLeads = async () => {
    try {
      const response = await api.get('/leads');
      setLeads(response.data?.items || []);
    } catch (error) {
      console.error('Erro ao carregar leads:', error);
      setLeads([]); // Set empty array on error
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title || !newTask.due_date) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      // Enviar data diretamente sem converter para UTC
      // Adiciona segundos se não existir no formato datetime-local
      const dueDate = newTask.due_date ? 
        (newTask.due_date.includes(':00') ? newTask.due_date : newTask.due_date + ':00') : null;
      
      await api.post('/tasks', {
        ...newTask,
        due_date: dueDate
      });
      
      toast.success('Tarefa criada com sucesso!');
      setShowAddModal(false);
      setNewTask({
        lead_id: '',
        title: '',
        description: '',
        due_date: ''
      });
      refresh();
    } catch (error) {
      toast.error('Erro ao criar tarefa');
      console.error(error);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await api.put(`/tasks/${taskId}/complete`);
      toast.success('Tarefa concluída!');
      refresh();
    } catch (error) {
      toast.error('Erro ao completar tarefa');
      console.error(error);
    }
  };

  const getLeadName = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    return lead ? lead.name : 'Lead não encontrado';
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date() && !tasks.find(t => t.due_date === dueDate)?.completed;
  };

  const getDaysUntilDue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getTaskPriority = (dueDate, completed) => {
    if (completed) return 'completed';
    
    const daysUntil = getDaysUntilDue(dueDate);
    if (daysUntil < 0) return 'overdue';
    if (daysUntil <= 1) return 'urgent';
    if (daysUntil <= 3) return 'high';
    return 'normal';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'overdue':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'urgent':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'high':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'overdue':
        return 'Atrasada';
      case 'urgent':
        return 'Urgente';
      case 'high':
        return 'Alta';
      case 'completed':
        return 'Concluída';
      default:
        return 'Normal';
    }
  };

  // Filter logic now handled by API
  
  const taskStats = {
    total: pagination.total_items || 0,
    pending: tasks.filter(t => !t.completed).length,
    completed: tasks.filter(t => t.completed).length,
    overdue: tasks.filter(t => !t.completed && isOverdue(t.due_date)).length
  };

  // Loading state with skeleton
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 bg-slate-200 rounded w-64 mb-2 animate-pulse"></div>
            <div className="h-4 bg-slate-200 rounded w-48 animate-pulse"></div>
          </div>
          <div className="h-10 bg-slate-200 rounded w-32 animate-pulse"></div>
        </div>
        
        <ListSkeleton items={isMobile ? 5 : 10} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tasks-page">
      {/* Header */}
      <div className={`flex items-center justify-between ${isMobile ? 'flex-col space-y-4' : 'flex-row'}`}>
        <div className={isMobile ? 'text-center' : ''}>
          <h1 className={`font-bold text-slate-900 ${isMobile ? 'text-2xl' : 'text-3xl'}`}>Tarefas</h1>
          <p className="text-slate-600 mt-1">Gerencie suas tarefas e follow-ups</p>
        </div>
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-task-button">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Nova Tarefa
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Criar Nova Tarefa</DialogTitle>
              <DialogDescription>Adicione uma nova tarefa para acompanhar seus leads</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="lead">Lead *</Label>
                <Select 
                  value={newTask.lead_id} 
                  onValueChange={(value) => setNewTask({...newTask, lead_id: value})}
                >
                  <SelectTrigger data-testid="task-lead-select">
                    <SelectValue placeholder="Selecione um lead" />
                  </SelectTrigger>
                  <SelectContent>
                    {(leads || []).map(lead => (
                      <SelectItem key={lead.id} value={lead.id}>
                        {lead.name} - {lead.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  data-testid="task-title-input"
                  value={newTask.title}
                  onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                  placeholder="Ex: Ligar para apresentar proposta"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  data-testid="task-description-input"
                  value={newTask.description}
                  onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                  placeholder="Detalhes sobre a tarefa..."
                  rows={3}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="due_date">Data de Vencimento *</Label>
                <Input
                  id="due_date"
                  type="datetime-local"
                  data-testid="task-due-date-input"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({...newTask, due_date: e.target.value})}
                  required
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAddTask} className="btn-primary" data-testid="save-task-button">
                Criar Tarefa
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="glass border-white/20">
          <CardContent className="p-6 text-center">
            <div className="text-2xl font-bold text-slate-900" data-testid="total-tasks-count">{taskStats.total}</div>
            <p className="text-sm text-slate-600">Total de Tarefas</p>
          </CardContent>
        </Card>
        
        <Card className="glass border-white/20">
          <CardContent className="p-6 text-center">
            <div className="text-2xl font-bold text-blue-600" data-testid="pending-tasks-count">{taskStats.pending}</div>
            <p className="text-sm text-slate-600">Pendentes</p>
          </CardContent>
        </Card>
        
        <Card className="glass border-white/20">
          <CardContent className="p-6 text-center">
            <div className="text-2xl font-bold text-green-600" data-testid="completed-tasks-count">{taskStats.completed}</div>
            <p className="text-sm text-slate-600">Concluídas</p>
          </CardContent>
        </Card>
        
        <Card className="glass border-white/20">
          <CardContent className="p-6 text-center">
            <div className="text-2xl font-bold text-red-600" data-testid="overdue-tasks-count">{taskStats.overdue}</div>
            <p className="text-sm text-slate-600">Atrasadas</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters - responsive */}
      <Card className="glass border-white/20">
        <CardContent className={`${isMobile ? 'p-4' : 'p-6'}`}>
          <div className={`${isMobile ? 'space-y-3' : 'flex items-center space-x-4'}`}>
            <Label>{isMobile ? 'Filtros' : 'Filtrar por:'}</Label>
            
            {/* Search for mobile */}
            {isMobile && (
              <Input
                placeholder="Buscar tarefas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-white/50"
              />
            )}
            
            <div className={`${isMobile ? 'flex flex-wrap gap-2' : 'flex space-x-2'}`}>
              {[
                { id: 'all', label: 'Todas' },
                { id: 'pending', label: 'Pendentes' },
                { id: 'completed', label: 'Concluídas' },
                { id: 'overdue', label: 'Atrasadas' }
              ].map((filterOption) => (
                <Button
                  key={filterOption.id}
                  variant={filter === filterOption.id ? 'default' : 'outline'}
                  size={isMobile ? 'sm' : 'sm'}
                  onClick={() => setFilter(filterOption.id)}
                  className={filter === filterOption.id ? 'btn-primary' : 'bg-white/60'}
                  data-testid={`filter-${filterOption.id}`}
                >
                  {filterOption.label}
                </Button>
              ))}
            </div>
            
            {/* Quick stats for mobile */}
            {isMobile && hasData && (
              <div className="flex justify-between items-center pt-2 text-sm text-slate-600">
                <span>{pagination.total_items} tarefas</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={refresh}
                  disabled={loading}
                >
                  🔄 Atualizar
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tasks List */}
      <div className="space-y-4" data-testid="tasks-list">
        {isEmpty ? (
          <Card className="glass border-white/20">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Nenhuma tarefa encontrada</h3>
              <p className="text-slate-500">
                {filter === 'all' 
                  ? 'Crie sua primeira tarefa para começar'
                  : 'Nenhuma tarefa corresponde ao filtro selecionado'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className={`${isMobile ? 'space-y-4' : 'space-y-4'}`}>
              {tasks
                .sort((a, b) => {
                  // Sort by completion status first, then by due date
                  if (a.completed !== b.completed) {
                    return a.completed ? 1 : -1;
                  }
                  return new Date(a.due_date) - new Date(b.due_date);
                })
            .map((task) => {
              const priority = getTaskPriority(task.due_date, task.completed);
              const daysUntil = getDaysUntilDue(task.due_date);
              
              return (
                <Card key={task.id} className={`glass border-white/20 card-hover ${task.completed ? 'opacity-75' : ''}`} data-testid={`task-card-${task.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      {/* Checkbox */}
                      <div className="flex items-center pt-1">
                        <Checkbox
                          checked={task.completed}
                          onCheckedChange={() => handleCompleteTask(task.id)}
                          disabled={task.completed}
                          data-testid={`task-checkbox-${task.id}`}
                        />
                      </div>
                      
                      {/* Task Content */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h3 className={`text-lg font-semibold ${task.completed ? 'line-through text-slate-500' : 'text-slate-900'}`}>
                              {task.title}
                            </h3>
                            <p className="text-sm text-slate-600 mt-1">
                              Lead: {getLeadName(task.lead_id)}
                            </p>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Badge className={getPriorityColor(priority)} data-testid={`task-priority-${task.id}`}>
                              {getPriorityLabel(priority)}
                            </Badge>
                          </div>
                        </div>
                        
                        {task.description && (
                          <p className={`text-sm mb-3 ${task.completed ? 'text-slate-400' : 'text-slate-600'}`}>
                            {task.description}
                          </p>
                        )}
                        
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center space-x-4 text-slate-500">
                            <span>
                              📅 {new Date(task.due_date).toLocaleDateString('pt-BR')} às {new Date(task.due_date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {user?.role === 'admin' && task.assigned_to_name && (
                              <span>👤 {task.assigned_to_name}</span>
                            )}
                            {!task.completed && (
                              <span className={priority === 'overdue' ? 'text-red-600 font-medium' : priority === 'urgent' ? 'text-orange-600 font-medium' : ''}>
                                {daysUntil < 0 
                                  ? `Atrasada ${Math.abs(daysUntil)} dia${Math.abs(daysUntil) !== 1 ? 's' : ''}`
                                  : daysUntil === 0 
                                    ? 'Vence hoje'
                                    : `${daysUntil} dia${daysUntil !== 1 ? 's' : ''} restante${daysUntil !== 1 ? 's' : ''}`
                                }
                              </span>
                            )}
                          </div>
                          
                          <span className="text-xs text-slate-400">
                            Criada em {new Date(task.created_at).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            </div>
            
            {/* Pagination */}
            {hasData && (
              <div className="mt-6">
                <Pagination
                  pagination={pagination}
                  onPageChange={goToPage}
                  onPageSizeChange={changePageSize}
                  loading={loading}
                  className="justify-center"
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Quick Tips */}
      {tasks.length > 0 && (
        <Card className="glass border-white/20">
          <CardContent className="p-6">
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-slate-800 mb-2">💡 Dicas de Produtividade</h4>
                <div className="text-sm text-slate-600 space-y-1">
                  <p>• <strong>Priorize tarefas</strong> por ordem de vencimento e importância</p>
                  <p>• <strong>Marque como concluída</strong> clicando no checkbox ao lado</p>
                  <p>• <strong>Use descrições claras</strong> para lembrar do contexto da tarefa</p>
                  <p>• <strong>Defina prazos realistas</strong> para manter a produtividade alta</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}